import { CommunityAssociation } from "@entities";
import { ExcelController } from "@controllers/Excel.controller";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { type FC, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { downloadExcelFile } from "../../helpers/excel/export";
import { KpiCard } from "../statistics/components/KpiCard";
import { KpiPieChart, type PieDatum } from "../statistics/components/KpiPieChart";
import { StatisticsDailyLineChart } from "../statistics/components/StatisticsDailyLineChart";
import { StatisticsVolunteerSimpleBarChart } from "../statistics/components/StatisticsVolunteerSimpleBarChart";
import CommunityMembersTable from "./CommunityMembersTable";
import {
  fetchCommunityDashboard,
  type CommunityAgeGenderRow,
  type CommunityEngagement,
} from "./communityDashboardApi";

const METHODOLOGY =
  "פילוח מגדר וגיל: מתנדבים עם תאריך לידה. התנדבויות: מאצ'ים מאושרים (גודל קבוצה לפי size), ללא בקשות סיוע. פעילים: התנדבות מאושרת ב-90 הימים האחרונים. שעות: דיווחי שעות של חברי הקהילה.";

type DashboardTab = "overview" | "members";
type ExportKind = "members" | "activity";

const AGE_BUCKET_ORDER = [
  "under18",
  "18_24",
  "25_34",
  "35_44",
  "45_54",
  "55_64",
  "65plus",
] as const;

function normalizeGenderLabel(gender: string) {
  const g = String(gender).toLowerCase();
  if (g.includes("female") || g.includes("נקב")) return "נקבה";
  if (g.includes("male") || g.includes("זכר")) return "זכר";
  return "אחר";
}

function buildDemographicPies(rows: CommunityAgeGenderRow[]) {
  const byBucket = new Map<string, { bucketLabel: string; count: number }>();
  const byGender = new Map<string, number>();

  for (const row of rows) {
    const existingBucket = byBucket.get(row.bucketKey) ?? {
      bucketLabel: row.bucketLabel,
      count: 0,
    };
    existingBucket.count += Number(row.count ?? 0);
    byBucket.set(row.bucketKey, existingBucket);

    const gender = normalizeGenderLabel(row.gender);
    byGender.set(gender, (byGender.get(gender) ?? 0) + Number(row.count ?? 0));
  }

  const agePie: PieDatum[] = AGE_BUCKET_ORDER.map((key) => byBucket.get(key))
    .filter(Boolean)
    .map((bucket) => ({
      name: bucket!.bucketLabel,
      value: bucket!.count,
    }));

  const genderOrder = ["זכר", "נקבה", "אחר"];
  const genderPie: PieDatum[] = genderOrder.map((gender) => ({
    name: gender,
    value: byGender.get(gender) ?? 0,
  }));

  return { agePie, genderPie };
}

function buildEngagementPie(engagement: CommunityEngagement | undefined): PieDatum[] {
  if (!engagement) return [];
  const slices = [
    { name: "פעילים ב-90 יום", value: engagement.activeLast90Days },
    { name: "התנדבו בעבר", value: engagement.volunteeredEarlier },
    { name: "לא התנדבו", value: engagement.neverVolunteered },
  ].filter((slice) => slice.value > 0);
  return slices;
}

function formatHours(value: number | undefined) {
  return (value ?? 0).toLocaleString("he-IL", { maximumFractionDigits: 1 });
}

const CommunityDashboard: FC = () => {
  const { communityId } = useParams<"communityId">();
  const navigate = useNavigate();
  const { currOrganization } = useOrganization();
  const { setAlertText, setSeverity } = useAlert();
  const [tab, setTab] = useState<DashboardTab>("overview");
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState<ExportKind | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["community-dashboard", communityId, currOrganization?.id],
    queryFn: () =>
      fetchCommunityDashboard({
        communityId: communityId!,
        organizationId: currOrganization!.id,
      }),
    enabled: !!communityId && !!currOrganization?.id,
    staleTime: 1000 * 60 * 2,
  });

  const { agePie, genderPie } = useMemo(
    () => buildDemographicPies(data?.ageGender ?? []),
    [data?.ageGender],
  );
  const engagementPie = useMemo(
    () => buildEngagementPie(data?.engagement),
    [data?.engagement],
  );
  const joinsChart = useMemo(
    () =>
      (data?.joinsByMonth ?? []).map((point) => ({
        dayLabel: point.label,
        value: point.count,
      })),
    [data?.joinsByMonth],
  );
  const participationsChart = useMemo(
    () =>
      (data?.participationsByMonth ?? []).map((point) => ({
        bucketLabel: point.label,
        value: point.count,
      })),
    [data?.participationsByMonth],
  );
  const subcategoryChart = useMemo(
    () =>
      (data?.topSubcategories ?? []).map((row) => ({
        bucketLabel: row.name,
        value: row.count,
      })),
    [data?.topSubcategories],
  );

  const exportCommunity = async (kind: ExportKind) => {
    if (!communityId || !currOrganization) return;
    setExportAnchor(null);
    setExporting(kind);
    try {
      const excelResult = await ExcelController.exportCommunityMembersToExcel(
        CommunityAssociation.hyperFilter({
          communityId,
          association: {
            $is: {
              organizationId: currOrganization.id,
              user: { $is: { isDeleted: false } },
            },
          },
        }),
        data?.community.name,
        kind,
      );
      if (excelResult) downloadExcelFile(excelResult);
    } catch {
      setAlertText("לא ניתן לייצא לאקסל");
      setSeverity("error");
    } finally {
      setExporting(null);
    }
  };

  if (!currOrganization) {
    return (
      <Stack width="100%" height="100%" paddingTop="10vh" alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (!communityId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">לא נמצא מזהה קהילה</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 0",
        flexDirection: "column",
        alignItems: "stretch",
        width: "100%",
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        bgcolor: "background.paper",
        borderRadius: "15px",
        px: 3,
        py: 2,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/communities")}
            sx={{ alignSelf: "flex-start", px: 0, minWidth: 0 }}
          >
            חזרה לקהילות
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: "primary.main",
                m: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {isLoading ? "טוען…" : (data?.community.name ?? "מסך שליטה בקהילה")}
            </Typography>
            <Tooltip
              title={METHODOLOGY}
              slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
            >
              <IconButton size="small" aria-label="הסבר על הנתונים">
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {data?.community.description ? (
            <Typography
              sx={{
                m: 0,
                color: "text.secondary",
                maxWidth: 720,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.community.description}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="outlined"
          startIcon={
            exporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileDownloadOutlinedIcon />
            )
          }
          disabled={!!exporting || isLoading}
          onClick={(event) => setExportAnchor(event.currentTarget)}
          sx={{ flexShrink: 0, mt: 0.5 }}
        >
          ייצוא לאקסל
        </Button>
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={() => setExportAnchor(null)}
        >
          <MenuItem onClick={() => exportCommunity("members")}>חברי הקהילה</MenuItem>
          <MenuItem onClick={() => exportCommunity("activity")}>
            פעילות חברי הקהילה
          </MenuItem>
        </Menu>
      </Box>

      {isError ? (
        <Typography color="error">לא ניתן לטעון את נתוני הקהילה</Typography>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, value: DashboardTab) => setTab(value)}
            sx={{ flexShrink: 0, minHeight: 40, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab value="overview" label="סקירה" sx={{ minHeight: 40 }} />
            <Tab value="members" label="חברים" sx={{ minHeight: 40 }} />
          </Tabs>

          {tab === "overview" ? (
            <Box
              sx={{
                flex: "1 1 0",
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                pb: 1,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(6, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  width: "100%",
                }}
              >
                <KpiCard
                  fullWidth
                  label="חברים בקהילה"
                  value={(data?.memberCount ?? 0).toLocaleString("he-IL")}
                  icon={<GroupsIcon />}
                  isLoading={isLoading}
                />
                <KpiCard
                  fullWidth
                  label="התנדבויות שבוצעו"
                  value={(data?.approvedParticipationsCount ?? 0).toLocaleString("he-IL")}
                  icon={<VolunteerActivismIcon />}
                  color="#00b4d8"
                  isLoading={isLoading}
                />
                <KpiCard
                  fullWidth
                  label="חברים חדשים (30 יום)"
                  value={(data?.newMembersLast30Days ?? 0).toLocaleString("he-IL")}
                  icon={<PersonAddAlt1Icon />}
                  isLoading={isLoading}
                />
                <KpiCard
                  fullWidth
                  label="פעילים (90 יום)"
                  value={(data?.activeMembersLast90Days ?? 0).toLocaleString("he-IL")}
                  icon={<HowToRegIcon />}
                  color="#06D6A0"
                  isLoading={isLoading}
                />
                <KpiCard
                  fullWidth
                  label="אחוז פעילים"
                  value={`${data?.activeMembersPercent ?? 0}%`}
                  icon={<PercentIcon />}
                  isLoading={isLoading}
                />
                <KpiCard
                  fullWidth
                  label="שעות מדווחות"
                  value={formatHours(data?.reportedHours)}
                  icon={<AccessTimeIcon />}
                  color="#ee923d"
                  isLoading={isLoading}
                />
              </Box>

              <Typography sx={{ m: 0, fontWeight: 600, color: "primary.main" }}>
                מגמות
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                  <StatisticsDailyLineChart
                    title="הצטרפות לקהילה לפי חודש"
                    color="#05204c"
                    data={joinsChart}
                    isLoading={isLoading}
                    xUnit="חודש"
                    denseFrom={8}
                  />
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                  <StatisticsVolunteerSimpleBarChart
                    title="התנדבויות לפי חודש"
                    data={participationsChart}
                    isLoading={isLoading}
                    valueLabel="התנדבויות"
                  />
                </Paper>
              </Box>

              <Typography sx={{ m: 0, fontWeight: 600, color: "primary.main" }}>
                פילוח
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                  gap: 2,
                }}
              >
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                  <KpiPieChart title="מעורבות" data={engagementPie} emptyLabel="אין נתונים" />
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                  <KpiPieChart title="מגדר" data={genderPie} emptyLabel="אין נתונים" />
                </Paper>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                  <KpiPieChart title="גיל" data={agePie} emptyLabel="אין נתונים" />
                </Paper>
              </Box>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: 1, borderColor: "divider" }}>
                <StatisticsVolunteerSimpleBarChart
                  title="תת-קטגוריות מובילות"
                  data={subcategoryChart}
                  isLoading={isLoading}
                  valueLabel="התנדבויות"
                />
              </Paper>
            </Box>
          ) : (
            <Box sx={{ flex: "1 1 0", minHeight: 0, width: "100%" }}>
              <CommunityMembersTable
                communityId={communityId}
                organizationId={currOrganization.id}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CommunityDashboard;
